import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';

import { ApiService, ConfigService } from '@app-core/services';
import { OVERLAY_DATA, OverlayRef } from '@app-core/services/overlay';
import * as customFormValidations from '@app-core/validations/forms/custom.validation';
import * as formValidations from '@app-core/validations/forms/generic.validation';
import { IUpdatePasswordRequest } from '@app-model/request';
import { ButtonComponent } from '@app-shared/components/button/button.component';
import { IconComponent } from '@app-shared/components/icon/icon.component';
import { InputControlComponent } from '@app-shared/components/input-control/input-control.component';
import { untilDestroyed } from '@app-shared/utils';
import * as fromRoot from '@app-store/reducers';
import { throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';

interface IUpdatePasswordForm {
  currentPassword: FormControl<string>;
  newPassword: FormControl<string>;
  confirmPassword: FormControl<string>;
}

@Component({
  templateUrl: './update-password-popup.component.html',
  styleUrls: ['./update-password-popup.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, InputControlComponent, IconComponent, ButtonComponent],
})
export class UpdatePasswordPopupComponent {
  data = inject<{
    username: string;
    stateToken?: string;
    flowType: 'OKTA' | 'LEGACY';
    actionType: 'UPDATE' | 'RESET';
  }>(OVERLAY_DATA);
  private store$ = inject<Store<fromRoot.State>>(Store);
  private apiService = inject(ApiService);
  private fb = inject(NonNullableFormBuilder);
  private ref = inject<OverlayRef<UpdatePasswordPopupComponent>>(OverlayRef);
  private _configService = inject(ConfigService);

  requestForm: FormGroup<IUpdatePasswordForm>;
  emailSuccess: boolean = false;

  private _isSSOFlow: boolean = this._configService.environment.authorization.flow === 'okta';

  readonly #untilDestroyed = untilDestroyed();

  constructor() {
    this.requestForm = this.fb.group({
      currentPassword: this.fb.control('', this.data?.flowType === 'LEGACY' ? [formValidations.RequiredValidator] : []),
      newPassword: this.fb.control('', [
        formValidations.RequiredValidator,
        customFormValidations.Pattern(
          this.data?.flowType === 'OKTA'
            ? /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[0-9])(?=.*[a-z]).{12,}$/
            : /^(?=.*[A-Z])(?=.*[!@#$%^&*_=+-])(?=.*[0-9])(?=.*[a-z]).{8,}$/,
          'Password does not meet requirements'
        ),
      ]),
      confirmPassword: this.fb.control('', [
        formValidations.RequiredValidator,
        customFormValidations.SameValue('newPassword', 'Both passwords should be the same'),
      ]),
    });
  }

  updatePassword(): void {
    if (this.requestForm.invalid) {
      return;
    }

    if (this._isSSOFlow && this.data?.flowType === 'OKTA' && this.data?.actionType === 'RESET') {
      this.apiService
        .resetOktaPassword(this.data.username, this.requestForm.get('newPassword').value, this.data.stateToken)
        .pipe(this.#untilDestroyed())
        .subscribe(() => (this.emailSuccess = true));
    } else {
      this.store$
        .select((store) => store.configState)
        .pipe(
          take(1),
          switchMap((data) => {
            const request: IUpdatePasswordRequest = {
              application: this._configService.appIdentifier,
              programId: data.clientConfig.programId,
              username: this.data.username,
              password: this.requestForm.get('currentPassword').value,
              newPassword: this.requestForm.get('newPassword').value,
              confirmNewPassword: this.requestForm.get('confirmPassword').value,
              noCaptcha: true,
            };

            return this.apiService.updatePassword(request);
          }),
          filter((data: any) => data && data.status),
          tap(() => {
            this.emailSuccess = true;
            this.requestForm.reset();
          }),
          catchError((err: Error) => {
            this.requestForm.reset();

            return throwError(() => err);
          }),
          this.#untilDestroyed()
        )
        .subscribe();
    }
  }

  close(): void {
    this.ref.close();
  }
}
